// @flow
import { useEffect, useState } from 'react';
import Debug from 'debug';
import Event from 'ol/events/Event';
import OlModify from 'ol/interaction/Modify';
import type { ModifyInteraction, ModifyEvent, Source, Collection } from '../types';
import { useMap } from '../context';
import { useLayer } from '../layers/context';
import { useSource } from '../sources/context';
import useEvent from './useEvent';

const debug = Debug('amms:modules:openlayers:interactions:modify');


type Props = $ReadOnly<{
    label?: string,
    source?: Source,
    features?: Collection,

    onChange?: (e: ModifyEvent) => void,
    onModifyStart?: (e: ModifyEvent) => void,
    onModify?: (e: Event) => void,
    onModifyEnd?: (e: ModifyEvent) => void,
    onPropertyChange?: (e: ModifyEvent) => void
}>;

function Modify({ label = '', source: externalSource, features, onChange, onModifyStart, onModify, onModifyEnd, onPropertyChange }: Props) {
    const [modify, setModify] = useState();
    const map = useMap();
    const layer = useLayer();
    const source = useSource();
    const _source = externalSource || source || (layer && layer.getSource());

    useEffect(() => {
        let _modify: ModifyInteraction;
        if(map && _source) {
            if(layer) {
                debug(`Adding modify interaction ${label} to layer: `, layer);
            }
            else {
                debug(`Adding modify interaction ${label} to map`);
            }

            _modify = new OlModify({
                label,
                source: _source,
                features
            });

            map.addInteraction(_modify);
            setModify(_modify);
        }

        return () => {
            if(map && _modify) {
                if(layer) {
                    debug(`Removing modify interaction ${label} from layer: `, layer);
                }
                else {
                    debug(`Removing modify interaction ${label} from map`);
                }

                map.removeInteraction(_modify);
                setModify(null);
            }
        };
    }, [label, map, layer, _source, features]);

    useEvent(modify, 'change', onChange);
    useEvent(modify, 'modifystart', onModifyStart);
    useEvent(modify, 'modify', onModify);
    useEvent(modify, 'modifyend', onModifyEnd);
    useEvent(modify, 'propertychange', onPropertyChange);

    return null;
}

export default Modify;